import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useAtom, useAtomValue } from 'jotai';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  locationsAccountAtom,
  locationsActivityAtom,
  locationsProductTypesAtom,
} from 'src/atoms/filters/locations';
import {
  FilterToggleChips,
  FilterToggleChipsProps,
} from 'src/common/components/Filters/FilterToggleChips';
import { pxToRem } from 'src/common/utils/fonts';
import { LocationsFiltersCountry } from 'src/locations/LocationsFiltersCountry';
import { LocationsLastRefresh } from 'src/locations/LocationsLastRefresh';
import { LocationsRefresh } from 'src/locations/LocationsRefresh';

const useActivityList = () => {
  const { t } = useTranslation();

  const activity = useMemo<FilterToggleChipsProps<number>['filterChipsOptions']>(
    () => [
      { value: 1, label: t('locations.filters.activity.24h'), color: 'green' },
      { value: 30, label: t('locations.filters.activity.30d'), color: 'amber' },
      { value: 90, label: t('locations.filters.activity.90d'), color: 'blue' },
    ],
    [],
  );

  return activity;
};

const useProductTypesList = () => {
  const { t } = useTranslation();
  const productTypes = useMemo<FilterToggleChipsProps<string>['filterChipsOptions']>(
    () => [
      { value: 'sim', label: t('locations.filters.productType.sim'), color: 'pink' },
      { value: 'rsim', label: t('locations.filters.productType.rsim'), color: 'teal' },
    ],
    [],
  );

  return productTypes;
};

export const LocationsFilters: FC = () => {
  const { t } = useTranslation();

  const account = useAtomValue(locationsAccountAtom);
  const [selectedProductTypes, setSelectedProductTypes] = useAtom(locationsProductTypesAtom);
  const [selectedActivity, setSelectedActivity] = useAtom(locationsActivityAtom);

  const productTypes = useProductTypesList();
  const activity = useActivityList();

  const noAccountSelected = account === null;

  return (
    <Card>
      <CardContent>
        <Typography
          data-testid="locations.filters.title"
          variant="h3"
          color="text.secondary"
          sx={{ height: pxToRem(18) }}
          gutterBottom
        >
          {t('locations.filters.title')}
        </Typography>
        <Stack useFlexGap spacing={pxToRem(16)}>
          <Stack alignItems="center" direction="row" useFlexGap spacing={16}>
            <LocationsFiltersCountry accountReferenceId={account} />
            <Stack direction="row" alignItems="center" useFlexGap columnGap={16}>
              <LocationsLastRefresh
                dataTestId="locations.filters.lastRefreshed"
                predicate={(query) => {
                  const hasMeta = query.meta !== undefined;
                  const isLocationsQuery = hasMeta && query.meta.isLocations === true;
                  return isLocationsQuery;
                }}
              />
              <LocationsRefresh
                data-testid="locations.filters.refresh"
                disabled={noAccountSelected}
                type="active"
                predicate={(query) => {
                  const hasMeta = query.meta !== undefined;
                  const isLocationsQuery = hasMeta && query.meta.isLocations === true;

                  return isLocationsQuery;
                }}
              />
            </Stack>
          </Stack>
          <Stack alignItems="center" direction="row" useFlexGap spacing={pxToRem(12)}>
            <Stack alignItems="center" direction="row" useFlexGap spacing={pxToRem(10)}>
              <Typography
                data-testid="locations.filters.productType.label"
                variant="text2"
                color="textSecondary"
              >
                {t('locations.filters.productType.label')}
              </Typography>
              <Stack
                data-testid="locations.filters.productType.buttons"
                direction="row"
                useFlexGap
                spacing={pxToRem(4)}
              >
                <FilterToggleChips
                  testId="productType"
                  type="multiple"
                  filterChipsOptions={productTypes}
                  selectedChips={selectedProductTypes}
                  onChange={setSelectedProductTypes}
                  disabled={noAccountSelected}
                />
              </Stack>
            </Stack>
            <Divider orientation="vertical" flexItem />
            <Stack alignItems="center" direction="row" useFlexGap spacing={pxToRem(10)}>
              <Typography variant="text2" color="textSecondary">
                {t('locations.filters.activity.label')}
              </Typography>
              <Stack direction="row" useFlexGap spacing={pxToRem(4)}>
                <FilterToggleChips
                  testId="activity"
                  type="single"
                  showAll={false}
                  filterChipsOptions={activity}
                  selectedChips={selectedActivity}
                  onChange={setSelectedActivity}
                  disabled={noAccountSelected}
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
